import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Sparkles } from "lucide-react";
import { cn } from "@/lib/utils";
import { SectionDivider } from "./shared";
import { getProposalTexts, type ProposalLanguage, type ProposalTexts } from "./i18n";

export type TimelineEntry = { label: string; when?: string };
export type CaseStudyEntry = { quote: string; author?: string; company?: string };
export type RecommendedPlan = {
  plan?: string;
  seats?: number;
  mrr_eur?: number;
  addons?: string[];
};
export type WhyUsEntry = { title: string; description?: string };

export type ProposalBodyData = {
  needs?: string[];
  pain_points?: string[];
  scope?: string[];
  how_we_work?: { intro?: string; steps?: string[] };
  outcomes?: string[];
  timeline?: TimelineEntry[];
  why_us?: Array<WhyUsEntry | string>;
  case_studies?: CaseStudyEntry[];
  recommended?: RecommendedPlan;
  next_steps?: string[];
};

export type BodySectionId =
  | "needs"
  | "pain_points"
  | "scope"
  | "how_we_work"
  | "outcomes"
  | "timeline"
  | "why_us"
  | "case_studies"
  | "recommended"
  | "next_steps";

export const BODY_SECTION_IDS: BodySectionId[] = [
  "needs",
  "pain_points",
  "scope",
  "outcomes",
  "timeline",
  "why_us",
  "case_studies",
  "next_steps",
];

export function hasBodyData(id: BodySectionId, data: ProposalBodyData | null | undefined): boolean {
  if (!data) return false;
  switch (id) {
    case "needs":
      return (data.needs?.length ?? 0) > 0;
    case "pain_points":
      return (data.pain_points?.length ?? 0) > 0;
    case "scope":
      return (data.scope?.length ?? 0) > 0;
    case "how_we_work":
      return Boolean(data.how_we_work?.intro?.trim()) || (data.how_we_work?.steps?.length ?? 0) > 0;
    case "outcomes":
      return (data.outcomes?.length ?? 0) > 0;
    case "timeline":
      return (data.timeline?.length ?? 0) > 0;
    case "why_us":
      return true;
    case "case_studies":
      return (data.case_studies?.length ?? 0) > 0;
    case "recommended":
      return Boolean(data.recommended?.plan) || (data.recommended?.addons?.length ?? 0) > 0;
    case "next_steps":
      return (data.next_steps?.length ?? 0) > 0;
  }
}

function SectionFrame({
  num,
  label,
  sectionWord,
  clientCompany,
  children,
}: {
  num: string;
  label: string;
  sectionWord: string;
  clientCompany?: string | null;
  children: React.ReactNode;
}) {
  return (
    <section className="pb-12 sm:pb-20">
      <SectionDivider num={num} label={label} sectionWord={sectionWord} clientCompany={clientCompany} />
      <div className="mt-8 sm:mt-10">{children}</div>
    </section>
  );
}

function EditorialList({
  items,
  ordered = false,
}: {
  items: string[];
  ordered?: boolean;
}) {
  if (items.length === 0) {
    return (
      <div className="border-y border-zinc-200 py-4 text-[15px] leading-[1.55] text-zinc-400">
        —
      </div>
    );
  }
  return (
    <ul className="divide-y divide-zinc-200 border-y border-zinc-200">
      {items.map((item, i) => (
        <li key={i} className="flex items-start gap-4 py-4">
          <span
            className={cn(
              "font-mono-tight mt-[3px] shrink-0 text-[11px] font-bold tabular-nums tracking-[0.05em]",
              ordered ? "brand-gradient-text" : "text-zinc-400"
            )}
          >
            {ordered ? String(i + 1).padStart(2, "0") : "—"}
          </span>
          <span className="break-words text-[15px] leading-[1.55] text-zinc-700">
            {item}
          </span>
        </li>
      ))}
    </ul>
  );
}

type SectionProps = {
  num: string;
  data: ProposalBodyData;
  language: ProposalLanguage;
  clientCompany?: string | null;
  mainProducts?: Array<{ name?: string; qty?: number }>;
};

export function ProposalNeeds({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  const items = data.needs ?? [];
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.needs} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      {items.length === 0 ? (
        <div className="border-y border-zinc-200 py-4 text-[15px] leading-[1.55] text-zinc-400">
          —
        </div>
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 sm:gap-4">
          {items.map((item, i) => (
            <div
              key={i}
              className="flex items-start gap-3.5 rounded-2xl bg-white p-4 ring-1 ring-zinc-200/80 sm:p-5"
            >
              <span
                className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-sm"
                style={{ background: "linear-gradient(135deg, #1E58FF 0%, #00FCCB 100%)" }}
                aria-hidden
              >
                <svg
                  viewBox="0 0 20 20"
                  width="16"
                  height="16"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M4 10.5l4 4 8-9" />
                </svg>
              </span>
              <div className="min-w-0 flex-1 pt-0.5">
                <div className="font-mono-tight text-[10px] font-bold uppercase tabular-nums tracking-[0.14em] text-zinc-400">
                  {String(i + 1).padStart(2, "0")}
                </div>
                <div className="mt-1.5 break-words text-[15px] font-medium leading-[1.5] text-zinc-900">
                  {item}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionFrame>
  );
}

export function ProposalPainPoints({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  const items = data.pain_points ?? [];
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.pain_points} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      {items.length === 0 ? (
        <div className="border-y border-zinc-200 py-4 text-[15px] leading-[1.55] text-zinc-400">
          —
        </div>
      ) : (
        <div className="grid gap-3 sm:gap-4">
          {items.map((item, i) => (
            <div
              key={i}
              className="group relative overflow-hidden rounded-2xl bg-amber-50/70 px-5 py-4 ring-1 ring-amber-200/80 sm:px-6 sm:py-5"
            >
              <span
                aria-hidden
                className="absolute inset-y-0 left-0 w-[3px]"
                style={{
                  background: "linear-gradient(180deg, #F59E0B 0%, #FBBF24 100%)",
                }}
              />
              <div className="flex items-start gap-4">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-amber-100 text-amber-700 ring-1 ring-amber-200">
                  <AlertTriangle className="h-4 w-4" />
                </span>
                <div className="min-w-0 flex-1 pt-0.5">
                  <div className="font-mono-tight text-[10px] font-bold uppercase tabular-nums tracking-[0.14em] text-amber-700">
                    {String(i + 1).padStart(2, "0")}
                  </div>
                  <div className="mt-1.5 break-words text-[15px] leading-[1.5] text-amber-950">
                    {item}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </SectionFrame>
  );
}

export function ProposalRecommendedCard({
  data,
  language,
  mainProducts,
  liveSeats,
  liveMrrEur,
}: {
  data: ProposalBodyData;
  language: ProposalLanguage;
  mainProducts?: Array<{ name?: string; qty?: number }>;
  liveSeats?: number | null;
  liveMrrEur?: number | null;
}) {
  const t = getProposalTexts(language);
  const r = data.recommended ?? {};
  // Plan, seats and MRR come from the selected products (the calculator) first —
  // that's where sales picks the actual plan and seat count. The brief's static
  // estimate is only a fallback when nothing has been selected yet.
  const productPlan = mainProducts?.find((p) => p.name && p.name.trim().length > 0)?.name?.trim();
  const productSeats = mainProducts?.find((p) => p.qty != null && p.qty > 0)?.qty ?? null;
  const planName = productPlan || r.plan?.trim();
  const seats = liveSeats ?? productSeats ?? r.seats;
  const mrrEur = liveMrrEur ?? r.mrr_eur;
  const hasPlan = Boolean(planName);
  return (
    <div>
      <p className="max-w-2xl text-[15px] leading-relaxed text-zinc-600 sm:text-base">
        {t.body.recommendedIntro}
      </p>
      <div className="mt-6 overflow-hidden rounded-2xl bg-gradient-to-br from-zinc-50 to-[#FAF7F1] p-5 ring-1 ring-zinc-200/70 sm:p-7">
        <div className="flex items-start gap-5">
          <span
            className="hidden h-12 w-12 shrink-0 items-center justify-center rounded-2xl text-white shadow-sm sm:flex"
            style={{ background: "linear-gradient(135deg, #1E58FF 0%, #00FCCB 100%)" }}
            aria-hidden
          >
            <Sparkles className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="font-mono-tight text-[10.5px] font-semibold uppercase tracking-[0.16em] text-zinc-500">
              {t.body.recommendedSeatPlanLabel}
            </div>
            <div
              className={cn(
                "mt-2 font-display text-2xl font-extrabold tracking-display sm:text-3xl",
                hasPlan ? "" : "text-zinc-400"
              )}
            >
              {planName ?? "—"}
            </div>
            {hasPlan && seats != null && (
              <div className="mt-1 text-sm text-zinc-500">
                {t.body.seatsSuffix(seats)}
                {mrrEur != null && <> · {t.body.mrrSuffix(mrrEur)}</>}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export function ProposalRecommended({ num, data, language, clientCompany, mainProducts }: SectionProps) {
  const t = getProposalTexts(language);
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.recommended} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      <ProposalRecommendedCard data={data} language={language} mainProducts={mainProducts} />
    </SectionFrame>
  );
}

export function ProposalScope({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  const items = data.scope ?? [];
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.scope} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      {items.length === 0 ? (
        <div className="border-y border-zinc-200 py-4 text-[15px] leading-[1.55] text-zinc-400">
          —
        </div>
      ) : (
        <div className="overflow-hidden rounded-2xl border border-zinc-200 bg-white">
          <div className="flex items-center justify-between border-b border-zinc-200 bg-zinc-50 px-5 py-3 sm:px-6">
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
              {t.body.workPlanHeader}
            </div>
            <div className="font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-500">
              {t.body.workPlanStatusHeader}
            </div>
          </div>
          <ol>
            {items.map((item, i) => (
              <li
                key={i}
                className="flex items-start gap-4 border-b border-zinc-200 px-5 py-4 last:border-b-0 sm:px-6 sm:py-5"
              >
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-zinc-900 font-mono text-[11px] font-bold text-white">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <div className="flex min-w-0 flex-1 flex-col gap-2 pt-0.5 sm:flex-row sm:items-start sm:gap-4">
                  <div className="min-w-0 flex-1 break-words text-[15px] font-semibold leading-[1.45] text-zinc-900">
                    {item}
                  </div>
                  <span className="shrink-0 self-start rounded-full bg-zinc-100 px-2.5 py-1 font-mono text-[9.5px] font-bold uppercase tracking-[0.14em] text-zinc-600 sm:self-center">
                    {t.body.workPlanStatusPlanned}
                  </span>
                </div>
              </li>
            ))}
          </ol>
        </div>
      )}
    </SectionFrame>
  );
}

export function ProposalHowWeWork({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  const intro = data.how_we_work?.intro?.trim() ?? "";
  const steps = data.how_we_work?.steps ?? [];
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.how_we_work} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      <p
        className={cn(
          "max-w-[60ch] text-[15px] leading-relaxed sm:text-[16px]",
          intro ? "text-zinc-700" : "text-zinc-400"
        )}
      >
        {intro || "—"}
      </p>
      <div className="mt-6">
        <EditorialList items={steps} ordered />
      </div>
    </SectionFrame>
  );
}

export function ProposalOutcomes({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  const items = data.outcomes ?? [];
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.outcomes} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      {items.length === 0 ? (
        <div className="border-y border-zinc-200 py-4 text-[15px] leading-[1.55] text-zinc-400">
          —
        </div>
      ) : (
        <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 p-5 ring-1 ring-emerald-200/60 sm:p-8">
          <div
            aria-hidden
            className="pointer-events-none absolute -right-16 -top-16 h-48 w-48 rounded-full opacity-30 blur-3xl"
            style={{ background: "linear-gradient(135deg, #00FCCB 0%, #1E58FF 100%)" }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-20 -left-12 h-56 w-56 rounded-full opacity-20 blur-3xl"
            style={{ background: "linear-gradient(135deg, #34D399 0%, #00FCCB 100%)" }}
          />
          <div className="relative">
            <div className="flex items-center gap-2">
              <span
                className="inline-flex items-center gap-1.5 rounded-full bg-white/70 px-2.5 py-1 font-mono text-[10px] font-bold uppercase tracking-[0.16em] text-emerald-700 shadow-sm ring-1 ring-emerald-200/70 backdrop-blur"
              >
                <Sparkles className="h-3 w-3" />
                {t.body.benefitsBadge}
              </span>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2 sm:gap-4">
              {items.map((item, i) => (
                <div
                  key={i}
                  className="group relative flex items-start gap-3.5 rounded-2xl bg-white/85 p-4 shadow-sm ring-1 ring-white backdrop-blur transition-all hover:-translate-y-0.5 hover:shadow-md sm:p-5"
                >
                  <span
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl text-white shadow-sm sm:h-10 sm:w-10"
                    style={{
                      background: "linear-gradient(135deg, #10B981 0%, #00FCCB 100%)",
                    }}
                    aria-hidden
                  >
                    <svg
                      viewBox="0 0 20 20"
                      width="16"
                      height="16"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M4 10.5l4 4 8-9" />
                    </svg>
                  </span>
                  <div className="min-w-0 flex-1 pt-0.5">
                    <div className="font-mono-tight text-[10px] font-bold uppercase tabular-nums tracking-[0.14em] text-emerald-700">
                      {t.body.benefitItemLabel} {String(i + 1).padStart(2, "0")}
                    </div>
                    <div className="mt-1.5 break-words text-[15px] font-medium leading-[1.5] text-zinc-900">
                      {item}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </SectionFrame>
  );
}

const TIMELINE_PHASE_RENAMES_LT: Record<string, string> = {
  "Atradimo ir planavimo etapas": "Adaptacijos, integracijų ir automatizacijų planavimo etapas",
  "Stabilizacijos ir optimizacijos laikotarpis": "Veikimo stabilizavimas ir optimizavimo laikotarpis",
};

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function ProposalTimeline({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  // Prepend the two evergreen post-acceptance steps that apply to every
  // proposal — contract drafting and the onboarding planning call — before any
  // per-proposal entries.
  const renames = language === "lt" ? TIMELINE_PHASE_RENAMES_LT : null;
  const entries: TimelineEntry[] = [
    ...t.body.timelineDefaults,
    ...(data.timeline ?? []).map((entry) =>
      renames && renames[entry.label]
        ? { ...entry, label: renames[entry.label] }
        : entry,
    ),
  ];

  const today = new Date();
  let lastResolvedDate: string | undefined;
  for (let i = entries.length - 1; i >= 0; i--) {
    const resolved = resolveTimelineDate(entries[i].when, today);
    if (resolved && ISO_DATE_RE.test(resolved)) {
      lastResolvedDate = resolved;
      break;
    }
  }
  const label = lastResolvedDate
    ? `${t.body.sectionLabels.timeline} ${language === "lt" ? "iki" : "by"} ${lastResolvedDate}`
    : t.body.sectionLabels.timeline;

  return (
    <SectionFrame num={num} label={label} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      <AnimatedTimeline entries={entries} />
    </SectionFrame>
  );
}

// Parses relative durations like "1 diena", "2 savaitės", "1-2 savaitės",
// "Day 1", "Week 1", "3 months" and returns an absolute date string offset from
// today. For ranges (e.g. "2-3 savaitės") uses the upper bound.
function resolveTimelineDate(when: string | undefined, from: Date): string | undefined {
  if (!when) return undefined;
  const numbers = when.match(/\d+/g);
  if (!numbers || numbers.length === 0) return when;
  const value = parseInt(numbers[numbers.length - 1], 10);
  if (!Number.isFinite(value)) return when;
  const lower = when.toLowerCase();
  let days: number;
  if (/savait|week/.test(lower)) {
    days = value * 7;
  } else if (/mėn|men\b|mėnesi|month/.test(lower)) {
    days = value * 30;
  } else {
    days = value;
  }
  const date = new Date(from);
  date.setDate(date.getDate() + days);
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function AnimatedTimeline({ entries }: { entries: TimelineEntry[] }) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [visibleSet, setVisibleSet] = useState<Set<number>>(() => new Set());
  const today = useRef(new Date()).current;

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    const items = Array.from(
      container.querySelectorAll<HTMLElement>("[data-timeline-index]")
    );
    if (items.length === 0) return;
    const io = new IntersectionObserver(
      (records) => {
        setVisibleSet((prev) => {
          let changed = false;
          const next = new Set(prev);
          for (const r of records) {
            if (!r.isIntersecting) continue;
            const idx = Number((r.target as HTMLElement).dataset.timelineIndex);
            if (!Number.isNaN(idx) && !next.has(idx)) {
              next.add(idx);
              changed = true;
            }
          }
          return changed ? next : prev;
        });
      },
      { threshold: 0.35, rootMargin: "0px 0px -10% 0px" }
    );
    items.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [entries.length]);

  if (entries.length === 0) {
    return (
      <div className="border-y border-zinc-200 py-4 text-[15px] leading-[1.55] text-zinc-400">
        —
      </div>
    );
  }

  const progress = entries.length <= 1
    ? "100%"
    : `${Math.round((Math.max(visibleSet.size - 1, 0) / (entries.length - 1)) * 100)}%`;

  return (
    <div ref={containerRef} className="noium-timeline-rail relative">
      <div className="noium-timeline-rail-track" aria-hidden>
        <div
          className="noium-timeline-rail-fill"
          style={{ ["--rail-progress" as string]: progress }}
        />
      </div>
      <ol className="space-y-6 sm:space-y-7">
        {entries.map((entry, i) => {
          const isVisible = visibleSet.has(i);
          const resolvedWhen = resolveTimelineDate(entry.when, today);
          return (
            <li
              key={i}
              data-timeline-index={i}
              data-visible={isVisible ? "true" : "false"}
              className="noium-timeline-item flex items-start gap-4"
              style={{
                transitionDelay: `${Math.min(i * 90, 540)}ms`,
                ["--dot-delay" as string]: `${(i % 4) * 280}ms`,
              }}
            >
              <span className="noium-timeline-dot" aria-hidden />
              <div className="min-w-0 flex-1 pt-[-1px]">
                <div className="flex items-center gap-2">
                  <span className="font-mono-tight text-[11px] font-bold tabular-nums tracking-[0.05em] brand-gradient-text">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  {resolvedWhen && (
                    <span className="rounded-full bg-zinc-100 px-2 py-0.5 font-mono text-[10px] font-semibold uppercase tracking-[0.12em] text-zinc-600">
                      {resolvedWhen}
                    </span>
                  )}
                </div>
                <div className="mt-1 break-words text-[15px] font-semibold leading-[1.45] text-zinc-900">
                  {entry.label}
                </div>
              </div>
            </li>
          );
        })}
      </ol>
    </div>
  );
}

export function ProposalWhyUs({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  const aiItems = (data.why_us ?? []).map((item) =>
    typeof item === "string" ? { title: item, description: undefined } : item
  );
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.why_us} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      <p className="max-w-2xl text-[15px] leading-relaxed text-zinc-600 sm:text-base">
        {t.body.whyUsIntro}
      </p>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 sm:gap-5">
        {t.body.whyUsItems.map((title, i) => {
          const description =
            aiItems[i]?.description?.trim() || t.body.whyUsDescriptions[i];
          return (
            <div
              key={i}
              className="relative overflow-hidden rounded-2xl bg-white p-5 ring-1 ring-zinc-200/80 sm:p-6"
            >
              <div
                aria-hidden
                className="absolute -right-8 -top-8 h-24 w-24 rounded-full opacity-10 blur-2xl"
                style={{ background: "linear-gradient(135deg, #1E58FF 0%, #00FCCB 100%)" }}
              />
              <div
                className="font-display text-3xl font-extrabold leading-none brand-gradient-text"
                style={{ letterSpacing: "-0.045em" }}
              >
                {String(i + 1).padStart(2, "0")}
              </div>
              <div className="mt-3 break-words text-[15px] font-semibold leading-[1.45] text-zinc-900">
                {title}
              </div>
              {description && (
                <div className="mt-2 break-words text-[14px] leading-[1.55] text-zinc-600">
                  {description}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </SectionFrame>
  );
}

export function ProposalCaseStudies({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  const entries = data.case_studies ?? [];
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.case_studies} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      {entries.length === 0 ? (
        <div className="border-y border-zinc-200 py-4 text-[15px] leading-[1.55] text-zinc-400">
          —
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          {entries.map((entry, i) => (
            <figure
              key={i}
              className="rounded-2xl bg-[#FAF7F1] p-5 ring-1 ring-zinc-200/70 sm:p-6"
            >
              <blockquote className="text-[15px] leading-relaxed text-zinc-800">
                &ldquo;{entry.quote}&rdquo;
              </blockquote>
              {(entry.author || entry.company) && (
                <figcaption className="mt-3 font-mono-tight text-[10.5px] uppercase tracking-[0.14em] text-zinc-500">
                  {[entry.author, entry.company].filter(Boolean).join(" · ")}
                </figcaption>
              )}
            </figure>
          ))}
        </div>
      )}
    </SectionFrame>
  );
}

export function ProposalNextSteps({ num, data, language, clientCompany }: SectionProps) {
  const t = getProposalTexts(language);
  return (
    <SectionFrame num={num} label={t.body.sectionLabels.next_steps} sectionWord={t.body.sectionWord} clientCompany={clientCompany}>
      <EditorialList items={data.next_steps ?? []} ordered />
    </SectionFrame>
  );
}

export const BODY_SECTION_COMPONENTS: Record<
  BodySectionId,
  React.ComponentType<SectionProps>
> = {
  needs: ProposalNeeds,
  pain_points: ProposalPainPoints,
  scope: ProposalScope,
  how_we_work: ProposalHowWeWork,
  outcomes: ProposalOutcomes,
  timeline: ProposalTimeline,
  why_us: ProposalWhyUs,
  case_studies: ProposalCaseStudies,
  recommended: ProposalRecommended,
  next_steps: ProposalNextSteps,
};

export function getBodySectionLabels(texts: ProposalTexts): Array<{ id: BodySectionId; label: string }> {
  return BODY_SECTION_IDS.map((id) => ({ id, label: texts.body.sectionLabels[id] }));
}
